import { useState, useEffect, useCallback, useRef } from 'react';
import { db, functions } from '../firebase';
import { collection, query, where, orderBy, limit, getDocs } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { useAuth } from '../context/AuthContext';
import Card from './ui/Card';
import Button from './ui/Button';
import Modal from './ui/Modal';
import { toast } from './ui/Toast';
import { useTranslation } from '../i18n/LanguageContext';
import {
  IconFileText,
  IconExternalLink,
  IconMail,
  IconRefresh,
  IconCheck,
  IconAlertTriangle,
} from '@tabler/icons-react';

// Injected here rather than relying on a stylesheet import, so the animation
// travels with the component.
const PULSE_KEYFRAMES = `
@keyframes agw-pulse {
  0%   { box-shadow: 0 0 0 0 rgba(59, 109, 17, 0.45); }
  50%  { box-shadow: 0 0 0 7px rgba(59, 109, 17, 0); }
  100% { box-shadow: 0 0 0 0 rgba(59, 109, 17, 0); }
}`;

const fmt = (ts, locale) => {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleString(locale, {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

export default function MachinePassportPanel({ machineId, machine, highlight = 0 }) {
  const { language } = useTranslation();
  const { orgId, role } = useAuth();
  const isManager = role === 'org_admin' || role === 'manager';

  const [exports, setExports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [latest, setLatest] = useState(null);

  const [showSend, setShowSend] = useState(false);
  const [recipient, setRecipient] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [pulsing, setPulsing] = useState(false);

  const panelRef = useRef(null);

  // Signposted from the Export PDF button: scroll the panel into view, let the
  // scroll settle, then pulse three times.
  useEffect(() => {
    if (!highlight) return;

    panelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });

    const start = setTimeout(() => setPulsing(true), 500);
    const stop = setTimeout(() => setPulsing(false), 500 + 3600);
    return () => { clearTimeout(start); clearTimeout(stop); };
  }, [highlight]);

  const pulseStyle = pulsing ? { animation: 'agw-pulse 1.2s ease-in-out 3' } : undefined;

  const load = useCallback(async () => {
    if (!orgId || !machineId) return;
    try {
      const snap = await getDocs(query(
        collection(db, 'passportExports'),
        where('orgId', '==', orgId),
        where('machineId', '==', machineId),
        orderBy('generatedAt', 'desc'),
        limit(10)
      ));
      setExports(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  }, [orgId, machineId]);

  useEffect(() => { load(); }, [load]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const generate = httpsCallable(functions, 'generateMachinePassport');
      const res = await generate({ orgId, machineId });
      setLatest(res.data);
      await load();
      toast.success('Passport generated');
    } catch (err) {
      console.error(err);
      toast.error(err?.message || 'Could not generate the passport');
    }
    setGenerating(false);
  };

  const handleSend = async () => {
    if (!recipient.trim() || !latestReference) return;
    setSending(true);
    try {
      const send = httpsCallable(functions, 'emailMachinePassport');
      await send({
        orgId,
        reference: latestReference,
        recipient: recipient.trim(),
        message: message.trim(),
      });
      setShowSend(false);
      setRecipient('');
      setMessage('');
      await load();
      toast.success('Passport sent');
    } catch (err) {
      console.error(err);
      toast.error(err?.message || 'Could not send the passport');
    }
    setSending(false);
  };

  const current = exports.find(e => !e.supersededBy && !e.revoked);
  const latestReference = latest?.reference || current?.reference;
  const previewUrl = latest?.previewUrl;

  // The serial is what identifies a machine once it leaves the fleet, so a
  // passport without one is worth warning about before it's issued.
  const missingSerial = !machine?.serialNumber;

  return (
    <div ref={panelRef} style={{ scrollMarginTop: 24 }}>
      <style>{PULSE_KEYFRAMES}</style>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div style={{ fontSize: 14, fontWeight: 400 }}>Machine passport</div>
        {isManager && (
          <Button
            size="sm"
            variant="secondary"
            icon={<IconFileText size={13} stroke={1.5} />}
            onClick={handleGenerate}
            disabled={generating}
            loading={generating}
            style={pulseStyle}
          >
            {exports.length ? 'Generate new' : 'Generate passport'}
          </Button>
        )}
      </div>

      <Card style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', lineHeight: 1.6 }}>
          A complete record of this machine — identity, current status, defect history with
          written release statements, and its inspection record. Issued in your organisation's
          name with a reference the recipient can verify.
        </div>

        {missingSerial && (
          <div style={{ marginTop: 12, background: 'var(--color-warning-light, #FDF3E3)', border: '0.5px solid var(--color-warning, #854F0B)', borderRadius: 'var(--radius-md)', padding: '9px 12px', display: 'flex', gap: 8 }}>
            <IconAlertTriangle size={15} color="var(--color-warning, #854F0B)" stroke={1.5} style={{ flexShrink: 0, marginTop: 1 }} />
            <div style={{ fontSize: 12, color: 'var(--color-warning, #854F0B)', lineHeight: 1.5 }}>
              This machine has no serial number recorded. The passport will still issue, but the
              serial is what identifies a machine after a change of ownership.
            </div>
          </div>
        )}

        {latest && (
          <div style={{ marginTop: 14, paddingTop: 14, borderTop: '0.5px solid var(--color-border-light)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <IconCheck size={15} color="var(--color-success)" stroke={1.5} />
              <div style={{ fontSize: 13, color: 'var(--color-text-primary)' }}>
                Ready — {latest.reference}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button
                size="sm"
                variant="secondary"
                icon={<IconExternalLink size={13} stroke={1.5} />}
                onClick={() => window.open(previewUrl, '_blank')}
              >
                Preview
              </Button>
              <Button
                size="sm"
                icon={<IconMail size={13} stroke={1.5} />}
                onClick={() => setShowSend(true)}
              >
                Send by email
              </Button>
            </div>
            <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginTop: 8 }}>
              Check the document before sending — the recipient receives exactly this file.
            </div>
          </div>
        )}
      </Card>

      {loading ? null : exports.length === 0 ? (
        <Card>
          <div style={{ fontSize: 13, color: 'var(--color-text-tertiary)' }}>
            No passport has been issued for this machine.
          </div>
        </Card>
      ) : (
        <Card>
          <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginBottom: 12, fontWeight: 400 }}>
            Issued passports
          </div>
          {exports.map(e => (
            <div
              key={e.id}
              style={{
                padding: '9px 0',
                borderBottom: '0.5px solid var(--color-border-light)',
                display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12,
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 12, fontFamily: 'var(--font-mono, monospace)', color: 'var(--color-text-primary)' }}>
                  {e.reference}
                </div>
                <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginTop: 3 }}>
                  {fmt(e.generatedAt, language)}
                  {e.generatedByName ? ` · ${e.generatedByName}` : ''}
                  {e.sendCount ? ` · sent ${e.sendCount}×` : ' · not sent'}
                </div>
              </div>
              <div style={{ fontSize: 11, flexShrink: 0, color: e.revoked
                ? 'var(--color-danger)'
                : e.supersededBy ? 'var(--color-text-tertiary)' : 'var(--color-success)' }}>
                {e.revoked ? 'Revoked' : e.supersededBy ? 'Superseded' : 'Current'}
              </div>
            </div>
          ))}
        </Card>
      )}

      <Modal
        open={showSend}
        onClose={() => setShowSend(false)}
        title="Send machine passport"
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowSend(false)}>Cancel</Button>
            <Button
              onClick={handleSend}
              disabled={sending || !recipient.trim()}
              loading={sending}
              icon={<IconMail size={14} stroke={1.5} />}
            >
              Send
            </Button>
          </>
        }
      >
        <div style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginBottom: 14, lineHeight: 1.5 }}>
          The passport is attached to the email and sent in your organisation's name.
          Every send is recorded against the document.
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>
            Recipient email <span style={{ color: 'var(--color-danger)' }}>*</span>
          </label>
          <input
            type="email"
            placeholder="buyer@example.co.uk"
            value={recipient}
            onChange={e => setRecipient(e.target.value)}
            style={{ width: '100%', boxSizing: 'border-box', fontFamily: 'inherit' }}
            autoFocus
          />
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>
            Message (optional)
          </label>
          <textarea
            placeholder="A short note to the recipient..."
            value={message}
            onChange={e => setMessage(e.target.value)}
            rows={3}
            style={{ width: '100%', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit' }}
          />
        </div>

        {latestReference && (
          <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>
            Sending reference <strong>{latestReference}</strong>
          </div>
        )}
      </Modal>
    </div>
  );
}
